/* eslint-disable array-callback-return */
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { TextField } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { isEmpty, isEqual } from "lodash";
import { pgcgtags } from "model/constants/reward-benchmark";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import interfaceInfo from "../../../assets/icons/InterfaceInfo.svg";
import { ControlType, DataTypes } from "../../../constants/app.constant";
import { FilterCategoryName, FilterNames } from "../../../constants/Filters";
import { PAGE_URL } from "../../../constants/urls.constants";
import { isParticipatePackage } from "../../../services/ReportsService";
import { RootState } from "../../../store/reducers";
import { setActiveFilter } from "../../../store/slices/filtersSlice";
import CheckBoxComponent from "../CommonComponents/CheckBoxComponent";
import CustomButtonComponent from "../CommonComponents/CustomButtonComponent";
import GroupMultiSelectDropDown from "../CommonComponents/GroupMultiSelectDropDown";
import MultipleInput from "../CommonComponents/MultipleInput";
import MultiSelectDropDown from "../CommonComponents/MultiSelectDropDown";
import RadioComponent from "../CommonComponents/RadioComponent";
import SliderInput from "../CommonComponents/SliderComponent";
import "./ReportFilters.scss";

interface ImenuItem {
  name: string;
  title: string;
  description: string;
  filters: any;
  peerGroups?: { key: string; label: string }[];
  customGrades?: { key: string; label: string }[];
  selectedCustomGrades?: string;
  setSelectedCustomGrades?: (value: string) => void;
  selectedFilterValues?: any;
  selectedPeerGroup?: string;
  setSelectedPeerGroup?: (value: string) => void;
  onFilterSelected: (selectedFilters: any) => void;
}

const ReportFilters: FC<ImenuItem> = ({
  name,
  title,
  description,
  filters = [],
  selectedPeerGroup,
  setSelectedPeerGroup,
  peerGroups,
  customGrades,
  selectedCustomGrades,
  setSelectedCustomGrades,
  selectedFilterValues = {},
  onFilterSelected
}) => {
  const { t } = useTranslation();
  let [selectedFilters, setSlectedFilters] = useState<any>({});
  const [isMultiSelectedFilters, setIsMultiSelectedFilters] =
    useState<boolean>(false);
  const [reportOverviewData, SetReportOverviewData] = useState<any>({});
  const { reportOverview } = useSelector((state: RootState) => {
    return state.filters;
  });

  const { isSJPR, selectedCountry, countryListAPIData } = useSelector(
    (state: RootState) => state.country
  );

  const currentCountryDetails = useMemo(() => {
    return countryListAPIData?.countryList?.find(
      (eachCountry: any) => eachCountry.countryCode === selectedCountry?.key
    );
  }, [countryListAPIData, selectedCountry?.key]);

  const isParticipatePkg = useMemo(() => {
    return !!(
      currentCountryDetails &&
      isParticipatePackage(currentCountryDetails?.productBrandId)
    );
  }, [currentCountryDetails]);

  const dispatch = useDispatch();

  const setActiveFilterName = (name: any) => {
    dispatch(setActiveFilter(name));
  };

  const getGroupSelectedOptionsCount = (
    groupOptions: any = {},
    subOptions: any = {}
  ) => {
    let selectedOptionsCount = 0;
    if (groupOptions && Object.keys(groupOptions).length) {
      for (const key in groupOptions) {
        if (key && key !== "All" && groupOptions[key]) {
          selectedOptionsCount += 1;
        }
      }
    }
    if (subOptions && Object.keys(subOptions).length) {
      for (const key in subOptions) {
        if (key && key !== "All" && subOptions[key]) {
          selectedOptionsCount += 1;
        }
      }
    }
    return selectedOptionsCount;
  };

  const onGroupMultiSelectChange = (
    filter: any,
    key: string,
    where: string,
    action?: string,
    groupKey?: string
  ) => {
    const filterName = action ? filter.groupName : filter.name;
    const selectedFilter = selectedFilters[filterName] || {};
    const isChecked = selectedFilter[key] ? false : true;
    const selectedParentFilter =
      selectedFilters[filter.groupName] ||
      selectedFilterValues[filter.groupName] ||
      {};
    const selectedChildFilter =
      selectedFilters[filter.name] || selectedFilterValues[filter.name] || {};
    if (action) {
      if (action === "All") {
        if (where === "DropDown") setIsMultiSelectedFilters(true);
        const selectFilterOptions: any = {};
        filter.options
          .filter((option: any) => option?.group?.key === key)
          .map((option: any) => {
            selectFilterOptions[option.key] = isChecked;
          });
        const _selectedFilters = {
          ...selectedFilters,
          [filter.groupName]: {
            ...selectedParentFilter,
            [key]: isChecked
          },
          [filter.name]: {
            ...selectedChildFilter,
            ...selectFilterOptions
          }
        };
        const totalSelectedOptions = getGroupSelectedOptionsCount(
          _selectedFilters[filter.groupName],
          _selectedFilters[filter.name]
        );

        if (totalSelectedOptions === 0) {
          _selectedFilters[filter.name]["All"] = false;
        } else if (totalSelectedOptions === filter?.totalOptions) {
          _selectedFilters[filter.name]["All"] = true;
        } else {
          _selectedFilters[filter.name]["All"] = "Indeterminate";
        }
        setSlectedFilters(_selectedFilters);
        return;
      }
      if (action === "CLEAR_ALL") {
        setSlectedFilters({
          ...selectedFilters,
          [filter.groupName]: {},
          [filter.name]: {}
        });
        return;
      }
    } else {
      if (where === "DropDown") setIsMultiSelectedFilters(true);
      if (key === "All") {
        //Select All Option Checked
        if (isChecked) {
          const selectGroupOptions: any = {};
          const selectSubOptions: any = {};
          filter.options.map((option: any) => {
            if (option?.group) {
              selectGroupOptions[option?.group?.key] = true;
            }
            selectSubOptions[option?.key] = true;
          });
          setSlectedFilters({
            ...selectedFilters,
            [filter.groupName]: selectGroupOptions,
            [filter.name]: {
              [key]: true,
              ...selectSubOptions
            }
          });
        }

        //Select All Option Un-Checked
        if (!isChecked) {
          setSlectedFilters({
            ...selectedFilters,
            [filter.groupName]: {},
            [filter.name]: {}
          });
        }
        return;
      } else {
        const _selectedFilters = {
          ...selectedFilters,
          [filter.groupName]: {
            ...selectedParentFilter
          },
          [filter.name]: {
            ...selectedChildFilter,
            [key]: isChecked
          }
        };

        const subDataOptions = filter.options.filter(
          (option: any) => option?.group?.key === groupKey
        );

        const selectedSubDataOptions = subDataOptions.filter(
          (option: any) => _selectedFilters[filter.name][option.key]
        );

        if (selectedSubDataOptions.length === 0) {
          _selectedFilters[filter.groupName][groupKey || ""] = false;
        } else if (selectedSubDataOptions.length === subDataOptions.length) {
          _selectedFilters[filter.groupName][groupKey || ""] = true;
        } else {
          _selectedFilters[filter.groupName][groupKey || ""] = "Indeterminate";
        }

        const totalSelectedOptions = getGroupSelectedOptionsCount(
          _selectedFilters[filter.groupName],
          _selectedFilters[filter.name]
        );

        if (totalSelectedOptions === 0) {
          _selectedFilters[filter.name]["All"] = false;
        } else if (totalSelectedOptions === filter?.totalOptions) {
          _selectedFilters[filter.name]["All"] = true;
        } else {
          _selectedFilters[filter.name]["All"] = "Indeterminate";
        }

        setSlectedFilters(_selectedFilters);
      }
    }
  };

  const onMultiSelectChange = (
    filter: any,
    key: string,
    isChecked: boolean,
    action?: string
  ) => {
    const selectedFilter =
      selectedFilters[filter.name] || selectedFilterValues[filter.name] || {};
    if (key === "All") {
      if (filter?.options?.length > 7) setIsMultiSelectedFilters(true);
      //Check All
      if (isChecked) {
        const selectFilterOptions: any = {};
        filter.options.map((option: any) => {
          selectFilterOptions[option.key] = true;
        });
        setSlectedFilters({
          ...selectedFilters,
          [filter.name]: selectFilterOptions
        });
      }

      //Un-Check All
      if (!isChecked) {
        setSlectedFilters({
          ...selectedFilters,
          [filter.name]: {}
        });
      }
      return;
    }

    if (key === "CLEAR_ALL") {
      setSlectedFilters({
        ...selectedFilters,
        [filter.name]: {}
      });
      return;
    }

    if (filter.controlType === ControlType.SINGLE_SELECT) {
      setSlectedFilters({
        ...selectedFilters,
        [filter.name]: {
          [key]: true
        }
      });
    } else {
      if (filter?.options?.length > 7 && !action)
        setIsMultiSelectedFilters(true);

      const _selectedFilters = {
        ...selectedFilters,
        [filter.name]: {
          ...selectedFilter,
          [key]: isChecked
        }
      };

      // Handle All checkbox Selection
      const selectedOpts =
        Object.keys(_selectedFilters[filter.name]).filter(
          (_key) => _selectedFilters[filter.name][_key] && _key !== "All"
        ) || [];

      if (selectedOpts.length === 0) {
        _selectedFilters[filter.name]["All"] = false;
      } else if (selectedOpts.length === filter.options.length - 1) {
        _selectedFilters[filter.name]["All"] = true;
      } else {
        _selectedFilters[filter.name]["All"] = "Indeterminate";
      }

      setSlectedFilters(_selectedFilters);
    }
  };

  const onMultiSelectClose = (
    event: any,
    reason: string,
    filterName: string
  ) => {
    setIsMultiSelectedFilters(false);
    const selectedFilter = selectedFilters[filterName] || {};
    const existSelectedFilter = selectedFilterValues[filterName] || {};
    if (
      ((selectedFilter && Object.keys(selectedFilter).length) ||
        (existSelectedFilter && Object.keys(existSelectedFilter).length)) &&
      !isEqual(selectedFilter, existSelectedFilter)
    ) {
      onFilterSelected(selectedFilters);
    }
  };

  const isReportDataLess = (name: string) => {
    if (name === FilterCategoryName?.downloadYourReport) {
      if (
        reportOverviewData?.jobRoleFeedback?.numberOfOrganizationsMarket < 4
      ) {
        return true;
      }
    }
    return false;
  };

  const noteMessageToDisplay = (name: string) => {
    if (
      name === FilterCategoryName?.organization ||
      name === FilterCategoryName?.market
    ) {
      return (
        <Box className="filterNoteContainer filter-note">
          <img src={interfaceInfo} alt="filterNote" />
          <Typography variant="h6" className="filterNote">
            {t("pa.filtersNoteMessage")}
          </Typography>
        </Box>
      );
    } else if (name === FilterCategoryName?.downloadYourReport) {
      if (isReportDataLess(name)) {
        return (
          <Box
            className={`exportYourReportClass ${
              isReportDataLess(name) && "exportYourReportClassWithNote"
            }`}
          >
            <Box className="filterNoteContainer">
              <img src={interfaceInfo} alt="filterNote" />
              <Typography variant="h6" className="filterNote">
                {t("pa.exportYourReportNoteMessage")}
              </Typography>
            </Box>
          </Box>
        );
      }
    } else {
      return "";
    }
  };

  useEffect(() => {
    if (!isMultiSelectedFilters) onFilterSelected(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    if (isEmpty(selectedFilterValues) && !isEmpty(selectedFilters)) {
      setSlectedFilters({});
    }

    if (!isEmpty(selectedFilterValues) && !isEmpty(selectedFilters)) {
      selectedFilters = { ...selectedFilterValues };
    }
  }, [selectedFilterValues]);

  useEffect(() => {
    SetReportOverviewData(reportOverview);
  }, [reportOverview]);

  useEffect(() => {
    if (
      (name === FilterCategoryName?.market ||
        name === FilterCategoryName?.organization) &&
      filters &&
      filters.length &&
      selectedFilterValues &&
      Object.keys(selectedFilterValues).length
    ) {
      const existingFilterValues = selectedFilterValues;
      const updatedFilterValues: any = {};
      for (const filterItem of filters) {
        if (
          existingFilterValues[filterItem.name] &&
          Object.keys(existingFilterValues[filterItem.name]).length
        ) {
          if (!filterItem.isSingleSelect) {
            if (filterItem.name === FilterNames.jobSubFamily) {
              const selectedDataOptions =
                filterItem.options.filter(
                  (option: any) =>
                    existingFilterValues[filterItem.name][option.key] &&
                    existingFilterValues[filterItem.groupName][
                      option?.group?.key
                    ]
                ) || [];
              if (selectedDataOptions && selectedDataOptions.length) {
                for (const option of selectedDataOptions) {
                  updatedFilterValues[filterItem.groupName] = {
                    ...(updatedFilterValues[filterItem.groupName] || {}),
                    [option?.group?.key]:
                      existingFilterValues[filterItem.groupName][
                        option?.group?.key
                      ]
                  };
                  updatedFilterValues[filterItem.name] = {
                    ...(updatedFilterValues[filterItem.name] || {}),
                    [option.key]: true
                  };
                }
                const totalSelectedOptions = Object.keys(
                  updatedFilterValues[filterItem.groupName]
                ).concat(Object.keys(updatedFilterValues[filterItem.name]));

                if (totalSelectedOptions.length === 0) {
                  updatedFilterValues[filterItem.name]["All"] = false;
                } else if (
                  totalSelectedOptions.length === filterItem?.totalOptions
                ) {
                  updatedFilterValues[filterItem.name]["All"] = true;
                } else {
                  updatedFilterValues[filterItem.name]["All"] = "Indeterminate";
                }
              } else {
                updatedFilterValues[filterItem.groupName] = {};
                updatedFilterValues[filterItem.name] = {};
              }
            } else {
              const nonAllOptions =
                filterItem.options.filter(
                  (option: any) => option.key !== "All"
                ) || [];
              
                
              const selectedDataOptions = filterItem.disabled
                ? nonAllOptions
                : nonAllOptions.filter(
                    (option: any) =>
                      existingFilterValues[filterItem.name][option.key]
                  );
              if (selectedDataOptions && selectedDataOptions.length) {
                for (const option of selectedDataOptions) {
                  updatedFilterValues[filterItem.name] = {
                    ...(updatedFilterValues[filterItem.name] || {}),
                    [option.key]: true
                  };
                }
                const totalSelectedOptions = Object.keys(
                  updatedFilterValues[filterItem.name]
                );

                if (totalSelectedOptions.length === 0) {
                  updatedFilterValues[filterItem.name]["All"] = false;
                } else if (
                  totalSelectedOptions.length ===
                  filterItem.options.length - 1
                ) {
                  updatedFilterValues[filterItem.name]["All"] = true;
                } else {
                  updatedFilterValues[filterItem.name]["All"] = "Indeterminate";
                }
              } else {
                updatedFilterValues[filterItem.name] = {};
              }
            }
          } else {
            updatedFilterValues[filterItem.name] =
              existingFilterValues[filterItem.name];
          }
        }
      }
      if (updatedFilterValues && Object.keys(updatedFilterValues).length) {
        setActiveFilterName("");
        setSlectedFilters(updatedFilterValues);
      }
    }
  }, [filters]);

  return (
    <Box className="report-filters">
      <Box
        className={`filter-header ${!isReportDataLess(name) && "filter-header-bottom-border"}`}
      >
        <Typography variant="h3" className="title">
          {title}
        </Typography>
        <Typography className="description">{description}</Typography>
      </Box>
      <Box className="filter-options">
        {noteMessageToDisplay(name)}
        {filters.map((filter: any) => {
          if (
            filter.controlType === ControlType.MULTI_SELECT &&
            filter?.options?.length <= 7
          ) {
            return (
              <CheckBoxComponent
                key={filter.name}
                name={filter.name}
                title={filter.title}
                description={filter.description}
                options={filter.options}
                values={selectedFilterValues[filter.name]}
                disabled={!!filter.disabled}
                onChange={(e: any, key: string) => {
                  if (filter.disabled) return;
                  setActiveFilterName(filter.name);
                  onMultiSelectChange(filter, key, e.target.checked);
                }}
              />
            );
          }
          if (
            filter?.tag === pgcgtags.PEER_GROUPS &&
            filter.options &&
            peerGroups
          ) {
            return (
              <Box key="peer-groups-block" className="dropdown-block">
                <Typography className="dropdown-block-title">
                  {filter.title}
                </Typography>
                <Typography
                  className="dropdown-block-description"
                  sx={{
                    fontSize: 12
                  }}
                >
                  {filter.description}
                </Typography>
                <Box className="dropdown-block-box">
                  <FormControl
                    fullWidth
                    size="small"
                    className="dropdown-control"
                  >
                    <Select
                      value={peerGroups?.length === 0 ? "" : selectedPeerGroup}
                      onChange={(e) => setSelectedPeerGroup?.(e.target.value)}
                      displayEmpty
                      className="dropdown-container"
                    >
                      <MenuItem value="">
                        <em className="placeholder-text">
                          {peerGroups?.length === 0
                            ? t("pa.noOptionAvailable")
                            : t("pa.selectAnOption")}
                        </em>
                      </MenuItem>
                      {peerGroups?.map((group) => (
                        <MenuItem key={group.key} value={group.key}>
                          {group.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {!isParticipatePkg && <CustomButtonComponent
                    variant="outlined"
                    onClick={() => {
                      window.open(
                        PAGE_URL.CREATE_PEER_GROUP + "/" + selectedCountry?.key,
                        "_self"
                      );
                    }}
                    icon={<KeyboardArrowRightIcon />}
                    text={t("pa.createpeergroup")}
                    iconPlacement="right"
                  />}
                </Box>
              </Box>
            );
          }

          if (
            filter?.tag === pgcgtags.CUSTOM_GRADES &&
            filter.options &&
            customGrades
          ) {
            return (
              <Box key="custom-grades-block" className="dropdown-block">
                <Typography className="dropdown-block-title">
                  {filter.title}
                </Typography>
                <Typography
                  className="dropdown-block-description"
                  sx={{
                    fontSize: 12
                  }}
                >
                  {filter.description}
                </Typography>{" "}
                <Box className="dropdown-block-box">
                  <FormControl
                    fullWidth
                    size="small"
                    className="dropdown-control"
                  >
                    <Select
                      value={
                        customGrades?.length === 0 ? "" : selectedCustomGrades
                      }
                      onChange={(e) => {
                        setSelectedCustomGrades?.(e.target.value);
                      }}
                      displayEmpty
                      className="dropdown-container"
                    >
                      <MenuItem value="">
                        <em className="placeholder-text">
                          {customGrades?.length === 0
                            ? t("pa.noOptionAvailable")
                            : t("pa.selectAnOption")}
                        </em>
                      </MenuItem>
                      {customGrades?.map((grade) => (
                        <MenuItem key={grade.key} value={grade.key}>
                          {grade.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>{" "}
                  {!isParticipatePkg && <CustomButtonComponent
                    variant="outlined"
                    onClick={() => {
                      window.open(
                        PAGE_URL.CREATE_CUSTOM_GRADE +
                          "/" +
                          selectedCountry?.key,
                        "_self"
                      );
                    }}
                    icon={<KeyboardArrowRightIcon />}
                    text={t("pa.createcustomgrade")}
                    iconPlacement="right"
                  />}
                </Box>
              </Box>
            );
          }

          if (
            filter.controlType === ControlType.SINGLE_SELECT &&
            filter?.options?.length <= 7
          ) {
            return (
              <RadioComponent
                key={filter.name}
                name={filter.name}
                title={filter.title}
                description={filter.description}
                options={filter.options}
                value={selectedFilterValues[filter.name]}
                onChange={(e: any, key: string) => {
                  setActiveFilterName(filter.name);
                  setSlectedFilters({
                    ...selectedFilters,
                    [filter.name]: key
                  });
                }}
              />
            );
          }

          if (
            filter?.options?.length > 7 &&
            filter.controlType !== ControlType.GROUP_MULTI_SELECT
          ) {
            return (
              <MultiSelectDropDown
                key={filter.name}
                name={filter.name}
                title={filter.title}
                description={filter.description}
                options={filter.options}
                isRadioOption={filter.controlType === ControlType.SINGLE_SELECT}
                disabled={!!filter.disabled}
                values={
                  selectedFilters[filter.name] ||
                  selectedFilterValues[filter.name] ||
                  {}
                }
                onChange={(key: string, action: string) => {
                  if (filter.disabled) return;
                  setActiveFilterName(filter.name);
                  const selectedFilter = selectedFilters[filter.name] || {};
                  const isChecked = selectedFilter[key] ? false : true;
                  onMultiSelectChange(filter, key, isChecked, action);
                }}
                onClose={(event: any, reason: string) => {
                  onMultiSelectClose(event, reason, filter.name);
                }}
              />
            );
          }

          if (filter.controlType === ControlType.GROUP_MULTI_SELECT) {
            return (
              <GroupMultiSelectDropDown
                width="532px"
                key={filter.name}
                groupName={filter.groupName}
                groupValues={
                  selectedFilters[filter.groupName] ||
                  selectedFilterValues[filter.groupName] ||
                  {}
                }
                name={filter.name}
                title={filter.title}
                description={filter.description}
                options={filter.options}
                totalOptions={filter?.totalOptions}
                isRadioOption={filter.controlType === ControlType.SINGLE_SELECT}
                disabled={!!filter.disabled}
                values={
                  selectedFilters[filter.name] ||
                  selectedFilterValues[filter.name] ||
                  {}
                }
                onChange={(
                  key: string,
                  action: string,
                  where: string,
                  groupKey?: string
                ) => {
                  if (filter.disabled) return;
                  setActiveFilterName(filter.groupName);
                  onGroupMultiSelectChange(
                    filter,
                    key,
                    where,
                    action,
                    groupKey
                  );
                }}
                onClose={(event: any, reason: string) => {
                  onMultiSelectClose(event, reason, filter.name);
                }}
              />
            );
          }

          if (filter.controlType === ControlType.MULTIPLE_INPUT) {
            return (
              <MultipleInput
                key={filter.name}
                name={filter.name}
                title={filter.title}
                description={filter.description}
                controls={filter.controls}
                values={selectedFilterValues[filter.name]}
                onChange={(e: any, key: string, controlName: string) => {
                  setActiveFilterName(filter.name);
                  setSlectedFilters({
                    ...selectedFilters,
                    [filter.name]: {
                      ...selectedFilters[filter.name],
                      [controlName]: e.target.value
                    }
                  });
                }}
              />
            );
          }
          if (filter.controlType === ControlType.NUMERIC) {
            return (
              <Box className="filter-section multiple-input-control">
                <h4
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    margin: 0,
                    marginBottom: "4px"
                  }}
                >
                  {t(filter?.title)}
                </h4>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#6B6B6B",
                    marginBottom: "8px"
                  }}
                >
                  {t(filter?.description)}
                </Typography>

                <Box className="input-controls-wrapper">
                  <TextField
                    key={`input-${filter.name}-numeric-${filter.name}`}
                    name={filter.name}
                    type={DataTypes?.NUMBER}
                    helperText={filter.controls?.helperText || ""}
                    placeholder={filter.controls?.placeholder || ""}
                    InputProps={{
                      inputProps: {
                        min: filter.controls?.min,
                        max: filter.controls?.max
                      }
                    }}
                    size="small"
                    sx={{ width: "150px" }}
                    value={selectedFilterValues[filter.name]}
                    onChange={(e) => {
                      setActiveFilterName(filter.name);
                      setSlectedFilters({
                        ...selectedFilters,
                        [filter.name]: e.target.value
                      });
                    }}
                    defaultValue={filter?.defaultValue}
                  />
                </Box>
              </Box>
            );
          }

          if (filter.controlType === ControlType.SLIDERINPUT) {
            return (
              <SliderInput
                key={filter.name}
                name={filter.name}
                title={filter.title}
                description={filter.description}
                controls={filter.controls}
                values={selectedFilterValues[filter.name]}
                onChange={(e: any, key: string, controlName: string) => {
                  setActiveFilterName(filter.name);
                  setSlectedFilters({
                    ...selectedFilters,
                    [filter.name]: e.target.value
                  });
                }}
                defaultValue={filter.defaultValue}
              />
            );
          }
        })}
      </Box>
    </Box>
  );
};

export default ReportFilters;
